import type { FormResponseValue, FormField, FormResponseValueAnswer } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";

/**
 * True when the answer matches any of the field's callout conditions, which is what flags a
 * response for a clinician's attention on the timeline. Conditions only ever compare equality, and
 * a multi-select answer matches if any selection matches.
 */
const isCalledOutForAnswer = (
  field: Pick<FormField, "calloutConditions">,
  value: FormResponseValueAnswer["value"]
): boolean => {
  if (!field.calloutConditions?.length) return false;
  if (value === undefined || value === null || value === "") return false;

  return field.calloutConditions.some((condition) =>
    Array.isArray(value) ? value.some((v) => v === condition.value) : value === condition.value
  );
};

/**
 * Stamps the per-response metadata the backend stores but never derives: everything downstream
 * consumers read off a submitted response rather than off the field it came from.
 *
 * This is client-owned by contract — `submit_form_response` writes `responses` verbatim, so any
 * key omitted here is absent from the stored response permanently. Notably `externalId` gates the
 * Healthie/Beluga/MDI push, `sharedWithEnduser` drives which answers the patient portal shows back,
 * and the field descriptions are the consent language reproduced in generated response PDFs.
 *
 * Value-dependent flags are recomputed from the answer on every call, so a callout that stops
 * applying after an edit is cleared rather than left stale.
 */
export const applyFieldResponseMetadata = <TResponse extends FormResponseValue>(
  response: TResponse,
  field: TellescopeModelFromName<"form_fields"> | undefined
): TResponse => {
  if (!field) return response;

  const result: TResponse = { ...response, isCalledOut: isCalledOutForAnswer(field, response.answer?.value) };
  if (field.description !== undefined) result.fieldDescription = field.description;
  if (field.htmlDescription !== undefined) result.fieldHtmlDescription = field.htmlDescription;
  if (field.externalId !== undefined) result.externalId = field.externalId;
  if (field.intakeField) result.intakeField = field.intakeField;
  if (field.sharedWithEnduser !== undefined) result.sharedWithEnduser = field.sharedWithEnduser;
  if (field.highlightOnTimeline !== undefined) result.isHighlightedOnTimeline = field.highlightOnTimeline;

  return result;
};
